/*
Copyright (c) 2024, HP Development Company, L.P. All rights reserved.
This software contains confidential and proprietary information of HP.
The user of this software agrees not to disclose, disseminate or copy
such Confidential Information and shall use the software only in accordance
with the terms of the license agreement the user entered into with HP.
*/
/* eslint-disable react/no-deprecated */

import { withAnalytics } from '@veneer/analytics';
import { Select, useDirection } from '@veneer/core';
import React, { useEffect, useRef, useState } from 'react';
import { generateID } from '../../base/helper';
import './styles.scss';
import { CountrySelectProps } from './types';

const TCountrySelect: React.FC<CountrySelectProps> = ({
  id = generateID('clientos-ui-toolkit-country-select'),
  value: defaultValue,
  countryOptions,
  supportiveSearchText,
  placeholderSearch,
  placeholderText,
  disabled,
  noResultsFoundText = 'No results found.',
  onChange
}) => {
  const [value, setValue] = useState(defaultValue ?? null);
  const [options, setOptions] = useState(countryOptions);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(undefined);
  const [focus, setFocus] = useState(false);
  const isRtl = useDirection().isRTL;

  const handleOnChange = (selectedOption) => {
    if (onChange) {
      onChange(selectedOption);
    }

    setValue(selectedOption.value);
  };

  const setClassName = () => {
    const selectedOption = countryOptions.find((opt) => opt.value === value);
    return selectedOption?.icon ? 'only-icon' : 'only-label';
  };

  const onSearch = (searchValue) => {
    const filtered = countryOptions.filter((item) =>
      item.label.toLowerCase().startsWith(searchValue.trim().toLowerCase())
    );
    setOptions(filtered);

    if (containerRef.current) {
      const pElement = containerRef.current.querySelector(
        'span[role="separator"]'
      ) as HTMLElement;

      if (pElement) {
        pElement.textContent = supportiveSearchText.replace(
          '{number}',
          filtered?.length.toString()
        );
      }
    }
    if (filtered?.length === 0) {
      setTimeout(() => {
        const noResultsFoundElement = containerRef.current.querySelector(
          'div[role="presentation"] > div > div:last-child'
        ) as HTMLInputElement;
        if (noResultsFoundElement) {
          noResultsFoundElement.textContent = noResultsFoundText;
        }

        const inputElement = containerRef.current.querySelector(
          'div[role="presentation"] input'
        ) as HTMLInputElement;

        if (inputElement) {
          setTimeout(() => {
            if (
              document.body.contains(inputElement) &&
              !inputElement.disabled
            ) {
              inputElement.focus();
            }
          }, 50);
        }
      }, 0);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const inputElement = containerRef.current.querySelector(
        'div[role="presentation"] input'
      ) as HTMLInputElement;

      if (inputElement) {
        inputElement.style.setProperty('direction', isRtl ? 'rtl' : 'ltr');
        inputElement.placeholder = placeholderSearch;
      }
    }
  }, [isOpen, isRtl, placeholderSearch]);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new MutationObserver(() => {
      const listBox = document.getElementById(`${id}-select-listbox`);
      if (isRtl) {
        listBox?.classList.add('rtl');
      } else {
        listBox?.classList.remove('rtl');
      }
      if (listBox) {
        setIsOpen(true);
        observer.disconnect(); // Disconnect after first match
      } else if (isOpen !== undefined) {
        setIsOpen(false);
      }
    });
    observer.observe(containerRef.current, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
    };
  }, [id, isOpen, isRtl]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setFocus(false);
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleFocusToggle = () => {
    if (!disabled) {
      setFocus(true);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const focusedOption = document.querySelector(
        '[data-focus="true"]'
      ) as HTMLElement;

      if (focusedOption) {
        focusedOption.scrollIntoView({ block: 'nearest' });
        focusedOption.setAttribute('data-focus', 'false');
      }
    }

    const handleFocusOption = (event: KeyboardEvent) => {
      if (!isOpen || !['ArrowDown', 'ArrowUp'].includes(event.key)) return;

      requestAnimationFrame(() => {
        const focusedOption = document.querySelector(
          '[data-focus="true"]'
        ) as HTMLElement;

        if (focusedOption) {
          focusedOption.scrollIntoView({ block: 'nearest' });
        }
      });
    };

    document.addEventListener('keydown', handleFocusOption);
    return () => {
      document.removeEventListener('keydown', handleFocusOption);
    };
  }, [isOpen]);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    if (isOpen !== undefined && !isOpen) {
      setTimeout(() => {
        const dummy = document.createElement('button');
        dummy.style.position = 'absolute';
        dummy.style.opacity = '0';
        dummy.style.pointerEvents = 'none';

        containerRef.current?.appendChild(dummy);
        dummy.focus();
        dummy.click();
        containerRef.current?.removeChild(dummy);
      }, 0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (containerRef.current) {
      const comboboxElement = containerRef.current.querySelector(
        'div[aria-haspopup="listbox"]'
      ) as HTMLElement;

      const openButtonElements = Array.from(
        document.getElementsByClassName('vn-select__open-button')
      ) as HTMLElement[];

      comboboxElement.setAttribute('tabindex', '-1');
      comboboxElement.setAttribute('aria-hidden', 'true');
      comboboxElement.removeAttribute('role');

      openButtonElements?.forEach((button) => {
        button.setAttribute('role', 'combobox');
        button.setAttribute(
          'aria-labelledby',
          comboboxElement?.getAttribute('aria-labelledby') ?? placeholderText
        );
        button.setAttribute('aria-label', placeholderText);
      });
    }
  }, [placeholderText]);

  useEffect(() => {
    const inputElement = containerRef.current.querySelector(
      `#${id} div[role="presentation"] > div > div > input`
    );
    if (inputElement) {
      inputElement.setAttribute('maxlength', '60');
    }
  }, [options, value, placeholderText, id]);

  return (
    <div
      id={id}
      className={`clientos-ui-toolkit-country-select ${setClassName()} ${isRtl ? 'rtl' : ''}`}
      ref={containerRef}
    >
      <Select
        className={`clientos-ui-toolkit-country-select__select ${isOpen ? 'open' : ''} ${focus ? 'focused' : ''} ${disabled ? 'disabled' : ''}`}
        id={`${id}-select`}
        showSearch
        clearIcon={false}
        visibleOptions={3}
        onSearch={onSearch}
        options={options}
        onChange={handleOnChange}
        onFocus={handleFocusToggle}
        value={[value]}
        placeholder={placeholderText}
        disabled={disabled}
        portalContainer={() => containerRef.current || document.body}
      />
    </div>
  );
};

TCountrySelect.displayName = 'CountrySelect';

export default TCountrySelect;
export const CountrySelectAnalytics = withAnalytics(TCountrySelect);
